import { useAuth } from './AuthContext'
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { getAccessContext } from '../services/access'
import { setApiTenantContext } from '../services/api'
import type { AccessContext, Organization, OrganizationBranding } from '../types/access'
import { brandingBanner, brandingColor, brandingLogo, brandingName, brandingOrgId, brandingThemeColor } from '../components/workspaces/admin/adminPanelUtils'
import { useTheme } from './ThemeContext'
import { isValidHexColor, meetsContrast, pickContrastText } from '../utils/contrast'

// Surface colors used to validate brand text contrast, matching the
// `--surface` token in globals.css for each theme. Kept in sync manually
// since brand colors must be validated before the stylesheet cascade runs.
const THEME_SURFACE: Record<'light' | 'dark', string> = {
  light: '#ffffff',
  dark: '#111a2e',
}

interface AccessContextValue {
  accessContext: AccessContext | null
  /**
   * Tenant explicitly selected in the UI (persisted in localStorage). Empty
   * for platform admins operating at platform scope — distinct from
   * `accessContext.tenantId`, which the backend may auto-resolve when only a
   * single tenant exists.
   */
  selectedTenantId: string
  currentOrganization: Organization | null
  currentBranding: OrganizationBranding | null
  currentBrandName: string
  currentLogoUrl: string
  currentBannerUrl: string
  brandStyleVars: React.CSSProperties
  getBrandingForOrg: (organizationId?: string | null) => OrganizationBranding | null
  loading: boolean
  error: string | null
  authenticationRequired: boolean
  reauthenticationTenantId: string
  refresh: () => Promise<void>
  setCurrentOrganizationId: (organizationId: string) => void
  setCurrentTenantId: (tenantId: string) => void
  hasPermission: (permissionKey: string) => boolean
  hasAnyPermission: (permissionKeys: string[]) => boolean
}

const TenantAccessContext = createContext<AccessContextValue | undefined>(undefined)

const permissionAliases: Record<string, string[]> = {
  'organizations.view': ['tenant.organizations.view'],
  'organizations.manage': ['tenant.organizations.manage', 'tenant.rbac.manage'],
  'organization_relationships.manage': ['tenant.organizations.manage', 'tenant.sharing.manage'],
  'org_units.view': ['tenant.organization_units.view', 'tenant.organization_units.manage'],
  'org_units.manage': ['tenant.organization_units.manage'],
  'users.view': ['tenant.users.view', 'tenant.users.manage', 'tenant.roles.view', 'tenant.roles.manage', 'tenant.rbac.manage'],
  'users.manage': ['tenant.users.manage', 'tenant.rbac.manage'],
  'users.invite': ['tenant.users.invite', 'tenant.users.manage', 'tenant.rbac.manage'],
  'users.reset_password': ['tenant.users.reset_password', 'tenant.users.manage', 'tenant.rbac.manage'],
  'roles.assign': ['tenant.scoped_roles.assign', 'tenant.roles.manage', 'tenant.rbac.manage'],
  'roles.view': ['tenant.roles.view', 'tenant.roles.manage', 'tenant.rbac.manage'],
  'roles.manage': ['tenant.roles.manage', 'tenant.rbac.manage'],
  'workers.view': ['tenant.workers.view', 'tenant.workers.manage', 'tenant.job_assignments.view'],
  'workers.manage': ['tenant.workers.manage'],
  'workers.invite': ['tenant.workers.invite', 'tenant.workers.manage'],
  'jobs.view': ['tenant.jobs.view', 'tenant.jobs.create', 'tenant.jobs.manage'],
  'jobs.manage': ['tenant.jobs.manage'],
  'locations.view': ['tenant.locations.view', 'tenant.locations.manage'],
  'locations.manage': ['tenant.locations.manage'],
  'timesheets.view': ['tenant.timesheets.view', 'tenant.punches.review', 'tenant.exports.csv'],
  'payroll.export': ['tenant.payroll.export', 'tenant.exports.csv'],
  'messages.view': ['tenant.messages.view', 'tenant.messages.send', 'tenant.locations.manage'],
  'branding.manage': ['tenant.branding.manage'],
  'settings.manage': ['tenant.settings.manage', 'tenant.rbac.manage'],
}

const permissionMatches = (permissionKeys: Set<string>, permission: string) => (
  permissionKeys.has(permission) || (permissionAliases[permission] || []).some((alias) => permissionKeys.has(alias))
)

const looksLikeRawException = (message: unknown) => {
  const value = String(message || '')
  return /\b(TypeError|ReferenceError|SyntaxError|RangeError|Error):/i.test(value)
    || /\bat\s+\S+\s+\(/.test(value)
    || /Cannot\s+(read|set)\s+propert/i.test(value)
    || /stack trace/i.test(value)
}

export const AccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { theme } = useTheme()
  const { user } = useAuth()
  const [storedAccessContext, setAccessContext] = useState<AccessContext | null>(null)
  const [currentTenantId, setCurrentTenantIdState] = useState(() => {
    if (typeof window === 'undefined') return ''
    try { return window.localStorage.getItem('punch.currentTenantId') || '' } catch { return '' }
  })
  const [currentOrganizationId, setCurrentOrganizationId] = useState('')
  const [requestLoading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authenticationRequired, setAuthenticationRequired] = useState(false)
  const [reauthenticationTenantId, setReauthenticationTenantId] = useState('')

  const selectionKey = JSON.stringify([user?.id || '', currentTenantId])
  const selected = useRef(selectionKey)
  selected.current = selectionKey
  const request = useRef({ generation: 0, controller: null as AbortController | null })
  const [resolvedSelection, setResolvedSelection] = useState('')
  // Render gates apply before effects: never expose an old identity's access.
  const accessContext = resolvedSelection === selectionKey ? storedAccessContext : null
  const loading = requestLoading || resolvedSelection !== selectionKey

  const refresh = useCallback(async () => {
    if (selected.current !== selectionKey) return
    request.current.controller?.abort()
    const controller = new AbortController()
    const generation = ++request.current.generation
    request.current.controller = controller
    const current = () => selected.current === selectionKey
      && generation === request.current.generation && !controller.signal.aborted
    if (!user?.id) {
      setAccessContext(null)
      setResolvedSelection(selectionKey)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setAuthenticationRequired(false)
    try {
      const context = await getAccessContext(currentTenantId || undefined, controller.signal)
      if (!current()) return
      if (context.currentUser?.id !== user.id || (currentTenantId && context.tenantId !== currentTenantId)) {
        throw new Error('Access context does not match the current identity.')
      }
      setAccessContext(context)
      // Keep the verified selected tenant on subsequent operations for every
      // multi-tenant owner; the server rechecks membership and MFA each time.
      setApiTenantContext(currentTenantId || null)
      const organizations = context.visibleOrganizations || []
      const currentId = context.isSystemSuperAdmin && !currentTenantId ? '' : context.currentOrganization?.id || organizations[0]?.id || ''
      setCurrentOrganizationId((existingId) => (
        existingId && organizations.some((org) => org.id === existingId) ? existingId : currentId
      ))
    } catch (err: any) {
      if (!current()) return
      setAccessContext(null)
      const status = err?.response?.status
      const reasonCode = err?.response?.data?.reasonCode
      const code = err?.response?.data?.code || err?.response?.data?.error?.code
      const needsAuthentication = status === 401 || ['mfa_required', 'mfa_enrollment_required', 'authentication_assurance_required', 'reauthentication_required', 'tenant_context_denied'].includes(code)
      setAuthenticationRequired(needsAuthentication)
      setReauthenticationTenantId(['mfa_required', 'mfa_enrollment_required', 'authentication_assurance_required', 'reauthentication_required'].includes(code) ? currentTenantId : '')
      const message = err?.response?.data?.message || err?.response?.data?.error?.message
      const safeMessage = looksLikeRawException(message) ? '' : message
      setError(status === 403
        ? safeMessage || `You are signed in, but this account does not have Admin Workspace access${reasonCode ? ` (${reasonCode})` : ''}.`
        : safeMessage || 'Unable to load your Admin workspace access right now.')
    } finally {
      if (current()) {
        setResolvedSelection(selectionKey)
        setLoading(false)
      }
    }
  }, [currentTenantId, selectionKey, user?.id])

  const setCurrentTenantId = useCallback((tenantId: string) => {
    if (tenantId === currentTenantId) return
    // Invalidate before React commits the selection, including uncancellable requests.
    selected.current = JSON.stringify([user?.id || '', tenantId])
    request.current.generation++
    request.current.controller?.abort()
    setAccessContext(null)
    setCurrentTenantIdState(tenantId)
    setCurrentOrganizationId('')
    // Apply immediately so requests issued before the context refresh lands
    // already carry the newly selected tenant (cleared when deselecting).
    setApiTenantContext(tenantId || null)
    if (typeof window !== 'undefined') {
      try {
        if (tenantId) window.localStorage.setItem('punch.currentTenantId', tenantId)
        else window.localStorage.removeItem('punch.currentTenantId')
      } catch { /* Selection stays in memory; verified server context remains authoritative. */ }
    }
  }, [currentTenantId, user?.id])

  useLayoutEffect(() => {
    void refresh()
    return () => {
      request.current.generation++
      request.current.controller?.abort()
    }
  }, [refresh])

  const permissionKeys = useMemo(
    () => new Set([
      ...(accessContext?.effectivePermissionKeys || []),
      ...(accessContext?.learnerPermissions || []),
      ...(accessContext?.mentorPermissions || []),
    ]),
    [accessContext?.effectivePermissionKeys, accessContext?.learnerPermissions, accessContext?.mentorPermissions],
  )
  const currentOrganization = useMemo(() => {
    const organizations = accessContext?.visibleOrganizations || []
    return organizations.find((org) => org.id === currentOrganizationId)
      || (accessContext?.isSystemSuperAdmin ? null : accessContext?.currentOrganization)
      || (accessContext?.isSystemSuperAdmin ? null : organizations[0])
      || null
  }, [accessContext?.currentOrganization, accessContext?.visibleOrganizations, currentOrganizationId])
  const brandingRows = accessContext?.branding?.organizations || []
  const brandingByOrgId = accessContext?.branding?.byOrgId || accessContext?.branding?.by_org_id || {}
  const getBrandingForOrg = useCallback((organizationId?: string | null) => {
    if (!organizationId) return null
    return brandingByOrgId[organizationId]
      || brandingRows.find((branding) => brandingOrgId(branding) === organizationId)
      || null
  }, [brandingByOrgId, brandingRows])
  const currentBranding = useMemo(
    () => getBrandingForOrg(currentOrganization?.id) || accessContext?.currentBranding || accessContext?.current_branding || accessContext?.brandingContext || null,
    [accessContext?.brandingContext, accessContext?.currentBranding, accessContext?.current_branding, currentOrganization?.id, getBrandingForOrg],
  )
  const currentBrandName = brandingName(currentBranding || undefined, currentOrganization?.name || accessContext?.tenant?.name || 'Punch')
  const currentLogoUrl = brandingLogo(currentBranding || undefined)
  const currentBannerUrl = brandingBanner(currentBranding || undefined)
  const brandStyleVars = useMemo(() => {
    const primary = brandingColor(currentBranding || undefined, 'primary')
    const secondary = brandingColor(currentBranding || undefined, 'secondary')
    const accent = brandingColor(currentBranding || undefined, 'accent')
    const background = brandingThemeColor(currentBranding || undefined, 'background')
    const surface = brandingThemeColor(currentBranding || undefined, 'surface')
    const text = brandingThemeColor(currentBranding || undefined, 'text')
    const button = brandingThemeColor(currentBranding || undefined, 'button') || primary
    const buttonText = brandingThemeColor(currentBranding || undefined, 'buttonText')
    // Invalid/malformed primary colors are ignored entirely — branding decorates,
    // it never gets to break the page, so an unparsable primary falls back to
    // the stylesheet's theme-safe default instead of being applied at all.
    if (!primary || !isValidHexColor(primary)) return {} as React.CSSProperties
    const navBase = secondary || primary
    const border = accent || primary
    const surfaceBg = THEME_SURFACE[theme]

    // `--brand-primary`/`--brand-button` are used for backgrounds and borders,
    // where any valid color is safe. Text-bearing tokens (`--link`,
    // `--brand-button-text`, `--brand-text`) are validated for contrast against
    // the current theme's surface color before being trusted as foreground text.
    const link = meetsContrast(primary, surfaceBg) ? primary : undefined
    const safeButton = isValidHexColor(button) ? button : primary
    const resolvedButtonText = isValidHexColor(buttonText) && meetsContrast(buttonText, safeButton)
      ? buttonText
      : pickContrastText(safeButton)
    const safeText = text && meetsContrast(text, surfaceBg) ? text : undefined

    return {
      '--brand-primary': primary,
      ...(secondary ? { '--brand-secondary': secondary } : {}),
      ...(accent ? { '--brand-accent': accent } : {}),
      // Brand backgrounds are decorative inputs, never semantic surfaces. Mix
      // them into the active theme surface so a dark brand palette cannot put
      // dark cards behind light-mode text (or the inverse in dark mode).
      ...(background ? { '--brand-background': `color-mix(in srgb, ${background} 10%, var(--bg))` } : {}),
      '--brand-surface': `color-mix(in srgb, ${surface || primary} 12%, var(--surface))`,
      ...(safeText ? { '--brand-text': safeText } : {}),
      '--brand-button': safeButton,
      '--brand-button-text': resolvedButtonText,
      '--brand-border': border,
      '--brand-surface-muted': `color-mix(in srgb, ${background || primary} 8%, var(--bg))`,
      '--brand-nav': `color-mix(in srgb, ${navBase} 10%, var(--surface))`,
      '--brand-focus': `${border}40`,            // ~25% tint — focus rings
      ...(link ? { '--link': link } : {}),
    } as React.CSSProperties
  }, [currentBranding, theme])

  useEffect(() => {
    if (typeof document === 'undefined') return
    // Apply to <body>, not <html>: `body.theme-dark` redeclares --primary,
    // --primary-strong, --surface-subtle, etc. directly on the body element,
    // which otherwise wins the cascade over an inline override sitting on an
    // ancestor (<html>) — silently dropping branding in dark mode.
    const root = document.body
    const vars = brandStyleVars as Record<string, string>
    const primary = vars['--brand-primary']
    const secondary = vars['--brand-secondary']
    const hasExplicitSurfaceBranding = Boolean(
      currentBranding?.backgroundColor
      || currentBranding?.background_color
      || currentBranding?.surfaceColor
      || currentBranding?.surface_color
    )

    // Brand-specific variables (new namespace, safe to add/remove freely)
    const brandVarKeys = [
      '--brand-primary', '--brand-secondary', '--brand-accent',
      '--brand-background', '--brand-surface', '--brand-text',
      '--brand-button', '--brand-button-text', '--brand-border',
      '--brand-surface-muted', '--brand-nav', '--brand-focus', '--link',
    ]
    brandVarKeys.forEach((key) => {
      if (vars[key]) root.style.setProperty(key, vars[key])
      else root.style.removeProperty(key)
    })

    // Brand colors may safely replace accent tokens. Semantic surface tokens
    // remain owned by the light/dark theme so foreground/background pairs
    // cannot be split by an arbitrary organization palette.
    if (primary) {
      root.style.setProperty('--primary', primary)
      root.style.setProperty('--primary-strong', secondary || primary)
      if (hasExplicitSurfaceBranding) root.style.removeProperty('--surface-subtle')
      else root.style.setProperty('--surface-subtle', `${primary}14`)
      if (vars['--brand-text']) root.style.setProperty('--text-primary', vars['--brand-text'])
    } else {
      root.style.removeProperty('--primary')
      root.style.removeProperty('--primary-strong')
      root.style.removeProperty('--text-primary')
      root.style.removeProperty('--surface-subtle')
    }
    // Remove stale inline surface overrides left by older provider versions.
    root.style.removeProperty('--surface-muted')
  }, [brandStyleVars, currentBranding])

  const value = useMemo<AccessContextValue>(
    () => ({
      accessContext,
      selectedTenantId: currentTenantId,
      currentOrganization,
      currentBranding,
      currentBrandName,
      currentLogoUrl,
      currentBannerUrl,
      brandStyleVars,
      getBrandingForOrg,
      loading,
      error,
      authenticationRequired,
      reauthenticationTenantId,
      refresh,
      setCurrentOrganizationId,
      setCurrentTenantId,
      hasPermission: (permissionKey: string) => permissionMatches(permissionKeys, permissionKey),
      hasAnyPermission: (permissions: string[]) => permissions.some((permission) => permissionMatches(permissionKeys, permission)),
    }),
    [reauthenticationTenantId, authenticationRequired, accessContext, brandStyleVars, currentBannerUrl, currentBrandName, currentBranding, currentLogoUrl, currentOrganization, currentTenantId, error, getBrandingForOrg, loading, permissionKeys, refresh, setCurrentTenantId],
  )

  return <TenantAccessContext.Provider value={value}>{children}</TenantAccessContext.Provider>
}

export const useAccessContext = () => {
  const context = useContext(TenantAccessContext)
  if (!context) throw new Error('useAccessContext must be used within AccessProvider')
  return context
}
